<template>
  <div class="pt-6">
    <v-row class="mb-5" dense>
      <v-col cols="6">
        <v-card
          variant="flat"
          rounded="lg"
          class="bg-green-lighten-5 py-4 text-center">
          <div class="text-h5 font-weight-bold text-success">
            {{ confirmedRows.length }}
          </div>
          <div class="text-body-2 text-success">Ознайомились</div>
        </v-card>
      </v-col>

      <v-col cols="6">
        <v-card
          variant="flat"
          rounded="lg"
          class="bg-grey-lighten-4 py-4 text-center">
          <div class="text-h5 font-weight-bold text-disabled">
            {{ pendingRows.length }}
          </div>
          <div class="text-body-2 text-disabled">Очікується</div>
        </v-card>
      </v-col>
    </v-row>

    <v-table
      v-if="participantRows.length"
      density="compact"
      class="rounded-lg overflow-hidden text-body-2">
      <thead class="bg-grey-lighten-5">
        <tr>
          <th class="px-4 py-3 text-uppercase text-disabled font-weight-bold">
            ПІБ
          </th>
          <th class="px-4 py-3 text-uppercase text-disabled font-weight-bold">
            Ознайомлений
          </th>
          <th
            class="px-4 py-3 text-uppercase text-disabled font-weight-bold text-no-wrap">
            Дата ознайомлення
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="participant in participantRows" :key="participant.key">
          <td class="px-4 py-2 font-weight-medium">
            {{ participant.name }}
          </td>
          <td class="px-4 py-2 font-weight-bold">
            <span
              :class="
                participant.isConfirmed ? 'text-success' : 'text-disabled'
              ">
              {{ participant.isConfirmed ? 'Так' : 'Ні' }}
            </span>
          </td>
          <td class="px-4 py-2 text-disabled text-no-wrap">
            {{
              new CustomDate(participant.confirmedAt).toString({
                time: true,
              }) || '---'
            }}
          </td>
        </tr>
      </tbody>
    </v-table>

    <div v-else class="text-center text-disabled py-8">
      Учасники ознайомлення відсутні
    </div>

    <v-divider class="mt-10 mb-4"></v-divider>

    <div class="d-flex justify-end">
      <v-btn variant="outlined" color="black" @click="dialog.close()">
        Закрити
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Dialog } from '@/dialog'
import {
  DocumentAcceptance,
  DocumentAcceptanceConfirmation,
} from '@/utils/types/document'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { ISODate } from 'best-modules/utils/customDate'

defineOptions({
  name: 'DocumentAcceptanceParticipants',
})

const props = defineProps<{
  dialog: Dialog
}>()

type ParticipantRow = {
  key: string
  name: string
  isConfirmed: boolean
  confirmedAt: ISODate | null
}

const acceptanceList = computed<DocumentAcceptance[]>(() => {
  return props.dialog.dialogData.acceptanceList || []
})

const acceptanceConfirmationList = computed<DocumentAcceptanceConfirmation[]>(
  () => {
    return props.dialog.dialogData.acceptanceConfirmationList || []
  }
)

const pendingRows = computed<ParticipantRow[]>(() => {
  return acceptanceList.value.flatMap(acceptance => {
    if (acceptance.acceptorTypeId === 1) {
      return [
        {
          key: `acceptance-${acceptance.id}-${acceptance.acceptor.id}`,
          name: getPersonName(acceptance.acceptor),
          isConfirmed: false,
          confirmedAt: null,
        },
      ]
    }

    return acceptance.acceptor.activeUsers.map(user => {
      return {
        key: `acceptance-${acceptance.id}-${user.id}`,
        name: getPersonName(user),
        isConfirmed: false,
        confirmedAt: null,
      }
    })
  })
})

const confirmedRows = computed<ParticipantRow[]>(() => {
  return acceptanceConfirmationList.value.map(confirmation => {
    return {
      key: `confirmation-${confirmation.id}`,
      name: getPersonName(confirmation.user),
      isConfirmed: true,
      confirmedAt: confirmation.confirmed_at,
    }
  })
})

const participantRows = computed<ParticipantRow[]>(() => {
  return [...pendingRows.value, ...confirmedRows.value]
})
</script>
